import type { DiscoverItem, Trip } from "../../types";
import { dayDate, resolveStopTimeLabel, tripStartOf } from "./trip-date";
import { migrateTrip } from "../../state/trip-storage";

/** URL-safe base64 encoding of a UTF-8 string. */
function b64urlEncode(s: string): string {
  const utf8 = new TextEncoder().encode(s);
  let bin = "";
  for (const byte of utf8) bin += String.fromCharCode(byte);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Inverse of b64urlEncode — returns the decoded string or null. */
export function b64urlDecode(s: string): string | null {
  try {
    const padded = s.replace(/-/g, "+").replace(/_/g, "/");
    const pad = padded.length % 4 ? 4 - (padded.length % 4) : 0;
    const bin = atob(padded + "=".repeat(pad));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** Build a shareable URL that reconstructs the trip on open. */
export function buildShareUrl(trip: Trip): string {
  const payload = JSON.stringify(trip);
  const hash = b64urlEncode(payload);
  const base = typeof window === "undefined" ? "" : window.location.origin;
  return `${base}/discover?t=${hash}`;
}

/** Parse a hash back into a Trip (or null on failure). */
export function parseShareHash(hash: string): Trip | null {
  const decoded = b64urlDecode(hash);
  if (!decoded) return null;
  try {
    const parsed = JSON.parse(decoded) as Trip;
    if (!parsed.id || !Array.isArray(parsed.days)) return null;
    // A link made before trips had dates or a party is still a valid trip.
    return migrateTrip(parsed);
  } catch {
    return null;
  }
}

/** Format a JS Date to an ICS-safe timestamp, e.g. 20260416T100000. */
function icsStamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    "00"
  );
}

/** Escape commas / newlines / semicolons for ICS text fields. */
function icsEscape(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/**
 * Generate an iCalendar (.ics) file body from a trip. Each stop
 * becomes a VEVENT pinned to its day's date, with the stop's
 * edited or default time window. Locations include the item's
 * city string.
 */
export function buildIcsFile(
  trip: Trip,
  resolveItem: (itemId: string) => DiscoverItem | null,
): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Explore Casablanca//Trip Planner//EN",
    "CALSCALE:GREGORIAN",
    `X-WR-CALNAME:${icsEscape(trip.name)}`,
  ];

  trip.days.forEach((day, dayIndex) => {
    const date = dayDate(tripStartOf(trip), dayIndex);
    day.stops.forEach((stop, stopIndex) => {
      const item = resolveItem(stop.itemId);
      if (!item) return;
      const label = resolveStopTimeLabel(stop, stopIndex);
      // Parse "HH:MM – HH:MM" (or default fallback) into start/end.
      const timeMatch = label.match(
        /(\d{1,2}:\d{2})\s*[–-]\s*(\d{1,2}:\d{2})/,
      );
      const startHHMM = timeMatch?.[1] ?? "10:00";
      const endHHMM = timeMatch?.[2] ?? "12:00";
      const [sh, sm] = startHHMM.split(":").map(Number);
      const [eh, em] = endHHMM.split(":").map(Number);
      const start = new Date(date);
      start.setHours(sh ?? 10, sm ?? 0, 0, 0);
      const end = new Date(date);
      end.setHours(eh ?? 12, em ?? 0, 0, 0);
      // End must be after start — if the user crossed midnight or
      // entered end < start, add a minimum 30-minute slot.
      if (end <= start) {
        end.setTime(start.getTime() + 30 * 60 * 1000);
      }
      lines.push(
        "BEGIN:VEVENT",
        `UID:${stop.id}@explore-casablanca`,
        `DTSTAMP:${icsStamp(new Date())}`,
        `DTSTART:${icsStamp(start)}`,
        `DTEND:${icsStamp(end)}`,
        `SUMMARY:${icsEscape(item.title)}`,
        `DESCRIPTION:${icsEscape(item.description + (stop.note ? `\n\nNote: ${stop.note}` : ""))}`,
        `LOCATION:${icsEscape(item.location)}`,
        "END:VEVENT",
      );
    });
  });

  lines.push("END:VCALENDAR");
  // RFC 5545 requires CRLF line endings.
  return lines.join("\r\n") + "\r\n";
}

/** Trigger a .ics download in the browser. */
export function downloadIcs(trip: Trip, ics: string): void {
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${trip.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "trip"}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
